import { AccessBadgeList } from './AccessBadgeList';
import type { ExtranetUserOutput, MemberStatus } from '../../types/extranet-api.types';
import { TableErrorRow } from '../common/TableErrorRow';
import { IconInbox } from '../incidents/icons';

interface MembersTableProps {
  items: ExtranetUserOutput[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  /** `userKey` du JWT — identifie la ligne de l'appelant. */
  currentUserKey: string;
  /** Nom de l'appelant (claims JWT) — le back ne le renvoie pas sur sa propre ligne. */
  currentUserFullName: string;
  onEditAccess: (member: ExtranetUserOutput) => void;
  onToggleStatus: (member: ExtranetUserOutput) => void;
}

const SKELETON_ROWS = 6;
const COL_COUNT = 5; // Nom · Email · Accès · Statut · Actions

export const memberDisplayName = (member: ExtranetUserOutput, fallback = '—'): string =>
  [member.firstName, member.lastName].filter(Boolean).join(' ') || fallback;

export const MembersTable = ({
  items,
  isLoading,
  isFetching,
  isError,
  error,
  currentUserKey,
  currentUserFullName,
  onEditAccess,
  onToggleStatus,
}: MembersTableProps) => (
  <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50/40">
            <Th>Nom</Th>
            <Th>Email</Th>
            <Th>Accès</Th>
            <Th>Statut</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} />)
          ) : isError ? (
            <TableErrorRow colSpan={COL_COUNT} error={error} fallbackTitle="Impossible de charger les membres" />
          ) : items.length === 0 ? (
            <EmptyRow />
          ) : (
            items.map(member => {
              const isSelf = member.userKey === currentUserKey;
              const isActive = member.status === 'ACTIVE';
              return (
                <tr
                  key={member.id}
                  className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60 transition-colors duration-100"
                >
                  <td className="px-4 py-3 text-zinc-900 whitespace-nowrap font-medium">
                    <span className="inline-flex items-center gap-2">
                      {memberDisplayName(member, isSelf ? currentUserFullName : '—')}
                      {isSelf ? (
                        <span className="rounded-md bg-zinc-100 text-zinc-600 text-[10px] font-medium px-1.5 py-0.5">
                          vous
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-700 whitespace-nowrap">
                    {member.email ?? <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <AccessBadgeList access={member.access} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <MemberStatusBadge status={member.status} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="inline-flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => onEditAccess(member)}
                        className="text-xs text-zinc-700 hover:text-zinc-900 hover:underline underline-offset-2"
                      >
                        Modifier les accès
                      </button>
                      {!isSelf ? (
                        <button
                          type="button"
                          onClick={() => onToggleStatus(member)}
                          className={[
                            'text-xs hover:underline underline-offset-2',
                            isActive ? 'text-red-700 hover:text-red-800' : 'text-emerald-700 hover:text-emerald-800',
                          ].join(' ')}
                        >
                          {isActive ? 'Désactiver' : 'Réactiver'}
                        </button>
                      ) : null}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const MemberStatusBadge = ({ status }: { status: MemberStatus }) => {
  const isActive = status === 'ACTIVE';
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-md text-xs font-medium px-2 py-0.5',
        isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500',
      ].join(' ')}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-400'}`} aria-hidden />
      {isActive ? 'Actif' : 'Désactivé'}
    </span>
  );
};

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SkeletonRow = () => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: COL_COUNT }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3 rounded-sm skeleton-pulse" style={{ width: `${[60, 75, 70, 40, 45][i] ?? 50}%` }} />
      </td>
    ))}
  </tr>
);

const EmptyRow = () => (
  <tr>
    <td colSpan={COL_COUNT} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucun membre sur cette page.</p>
          <p className="text-xs text-zinc-500 mt-1">Invitez un membre depuis l&apos;onglet Invitations.</p>
        </div>
      </div>
    </td>
  </tr>
);
